// The extension opens the cabinet with `?ext_challenge=<PKCE S256 challenge>`.
// The challenge is kept for the tab (sessionStorage) for a short while, so the
// "Расширение" page still knows about the pending request when it is reached
// by another route — after a login redirect, or from the side menu.

const KEY = 'od.ext_challenge';
export const EXT_CHALLENGE_TTL_MS = 10 * 60_000;

/** base64url(sha256) is 43 chars; the server accepts 43–128. */
const CHALLENGE_RE = /^[A-Za-z0-9_-]{43,128}$/;

export function isValidExtChallenge(value: unknown): value is string {
  return typeof value === 'string' && CHALLENGE_RE.test(value);
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function storeExtChallenge(challenge: string, now: number = Date.now()): void {
  if (!isValidExtChallenge(challenge)) return;
  try {
    storage()?.setItem(KEY, JSON.stringify({ challenge, at: now }));
  } catch {
    // storage unavailable — the challenge then lives only in the URL, as before.
  }
}

/** The stored challenge if it is younger than EXT_CHALLENGE_TTL_MS; a stale or malformed entry is dropped. */
export function readExtChallenge(now: number = Date.now()): string | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { challenge?: unknown; at?: unknown };
    const fresh = typeof parsed.at === 'number' && now - parsed.at >= 0 && now - parsed.at < EXT_CHALLENGE_TTL_MS;
    if (!fresh || !isValidExtChallenge(parsed.challenge)) {
      s.removeItem(KEY);
      return null;
    }
    return parsed.challenge;
  } catch {
    try {
      s.removeItem(KEY);
    } catch {
      // ignore
    }
    return null;
  }
}

export function clearExtChallenge(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // nothing to clear
  }
}

/** Pulls `ext_challenge` out of a router `search` string and remembers it. Returns it when valid. */
export function captureExtChallenge(search: string): string | null {
  const value = new URLSearchParams(search).get('ext_challenge');
  if (!isValidExtChallenge(value)) return null;
  storeExtChallenge(value);
  return value;
}
